import React, { useState, useEffect } from "react";

function PrayerTimes() {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fixed location: Ottawa, Ontario (ISNA method)
  const city = "Ottawa";
  const country = "Canada";
  const method = 2; // ISNA

  useEffect(() => {
    async function fetchPrayerTimes() {
      setLoading(true);
      try {
        const res = await fetch(
          `https://api.aladhan.com/v1/timingsByCity?city=${city}&country=${country}&method=${method}`
        );
        const data = await res.json();

        // Extract relevant prayer times
        const filtered = (({ Imsak, Fajr, Sunrise, Dhuhr, Asr, Sunset, Maghrib, Isha }) => ({
          Imsak,
          Fajr,
          Sunrise,
          Dhuhr,
          Asr,
          Sunset,
          Maghrib,
          Isha,
        }))(data.data.timings);

        // Helper function to shift time by minutes (+/-)
        const adjustTime = (timeStr, offsetMinutes) => {
          const [hour, minute] = timeStr.split(":").map(Number);
          const date = new Date();
          date.setHours(hour, minute + offsetMinutes);
          const newHours = date.getHours().toString().padStart(2, "0");
          const newMinutes = date.getMinutes().toString().padStart(2, "0");
          return `${newHours}:${newMinutes}`;
        };

        // Apply your adjustments
        const adjustedFajr = adjustTime(filtered.Fajr, -6);       // Fajr -6 mins
        const adjustedMaghrib = adjustTime(filtered.Maghrib, 18); // Maghrib +18 mins
        const adjustedImsak = adjustTime(adjustedFajr, -15);      // Imsak = 15 mins before adjusted Fajr

        const adjusted = {
          ...filtered,
          Imsak: adjustedImsak,
          Fajr: adjustedFajr,
          Maghrib: adjustedMaghrib,
        };

        setPrayerTimes(adjusted);
      } catch (error) {
        console.error("Error fetching prayer times:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchPrayerTimes();
  }, []);

  // Format today's date nicely
  const formattedDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.loadingText}>Loading prayer times for Ottawa...</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Ottawa Prayer Times</h1>
        <p style={styles.date}>{formattedDate}</p>
        <p style={styles.subtitle}>Updated daily — ISNA calculation method</p>
      </div>

      <div style={styles.card} className="fade-in">
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Prayer</th>
              <th style={styles.th}>Time</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(prayerTimes).map(([name, time]) => (
              <tr key={name} style={styles.row} className="row-hover">
                <td style={styles.td}>{name}</td>
                <td style={styles.time}>{time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={styles.note}>
        * Times are automatically fetched for Ottawa, Ontario (ISNA Method)
      </p>

      {/* Inline keyframe styles for fade & hover */}
      <style>
        {`
          .fade-in {
            animation: fadeIn 0.8s ease forwards;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .row-hover:hover {
            background-color: #f2fdf8;
            transform: scale(1.01);
            transition: all 0.3s ease;
          }

          @media (max-width: 600px) {
            table {
              width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: "#fff",
    fontFamily: "'Georgia', serif",
    padding: "0px 25px",
    textAlign: "center",
    color: "#333",
  },
  header: {
    marginBottom: "0px",
  },
  title: {
    color: "#067446ff",
    fontSize: "2.2rem",
    marginBottom: "5px",
    fontWeight: "400",
    letterSpacing: "0.5px",
  },
  date: {
    color: "#555",
    fontSize: "1.1rem",
    marginBottom: "6px",
    fontStyle: "italic",
  },
  subtitle: {
    color: "#d19f14ff",
    fontSize: "1rem",
  },
  card: {
    backgroundColor: "#f9f9f9",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
    padding: "0px",
    maxWidth: "700px",
    margin: "0 auto 35px auto",
    transition: "box-shadow 0.3s ease",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "center",
    borderRadius: "12px",
    overflow: "hidden",
  },
  th: {
    backgroundColor: "#067446ff",
    color: "#fff",
    padding: "12px 10px",
    fontSize: "1rem",
    letterSpacing: "0.5px",
  },
  row: {
    transition: "all 0.3s ease",
  },
  td: {
    padding: "12px 10px",
    borderBottom: "1px solid #eee",
    textTransform: "capitalize",
    color: "#333",
  },
  time: {
    padding: "12px 10px",
    borderBottom: "1px solid #eee",
    color: "#067446ff",
  },
  note: {
    marginTop: "10px",
    color: "#777",
    fontSize: "0.9rem",
  },
  loadingText: {
    color: "#067446ff",
  },
};

export default PrayerTimes;
